import { useState, useCallback, useEffect, useRef } from "react";
import {
  getAllWaitlistUsers,
  getWaitlistStats,
  getSignupsByDay,
  updateUserStatus,
  deleteUser,
  bulkUpdateStatus,
  bulkDeleteUsers,
  subscribeToWaitlist,
  exportToCSV,
  copyEmailsToClipboard,
} from "../lib/supabase";

const ADMIN_PASSWORD = "Alphastack@847103";
const SESSION_KEY = "as_admin_v2";

/**
 * useAdmin
 * Complete admin state machine:
 *  - Authentication (password-based, session-persisted)
 *  - User data fetching with search/pagination
 *  - Stats & chart data
 *  - Realtime subscription
 *  - Bulk operations
 *  - Export/copy helpers
 */
export function useAdmin() {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "true"
  );
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);

  const login = (password) => {
    if (loginAttempts >= 5) {
      setLoginError("Too many failed attempts. Refresh to try again.");
      return;
    }
    setLoginLoading(true);
    setLoginError("");

    // Intentional small delay to prevent brute-force timing
    setTimeout(() => {
      if (password === ADMIN_PASSWORD) {
        sessionStorage.setItem(SESSION_KEY, "true");
        setIsAuthenticated(true);
        setLoginAttempts(0);
      } else {
        setLoginAttempts((n) => n + 1);
        setLoginError("Incorrect password. Access denied.");
      }
      setLoginLoading(false);
    }, 600);
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
    setUsers([]);
    setStats({ total: 0, today: 0, thisWeek: 0, thisMonth: 0 });
  };

  // ── Data ──────────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState("");

  // Search / pagination state
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // Stats
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0, thisMonth: 0 });
  const [statsLoading, setStatsLoading] = useState(false);

  // Chart
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Realtime indicator
  const [realtimeActive, setRealtimeActive] = useState(false);
  const [newSignupFlash, setNewSignupFlash] = useState(null);
  const unsubRef = useRef(null);

  // ── Fetch users ────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async (opts = {}) => {
    setDataLoading(true);
    setDataError("");

    const { data, error, total } = await getAllWaitlistUsers({
      search: opts.search ?? search,
      limit: PAGE_SIZE,
      offset: (opts.page ?? page) * PAGE_SIZE,
    });

    if (error) {
      setDataError("Failed to load data. Check your Supabase credentials.");
    } else {
      setUsers(data);
      setTotalCount(total);
    }
    setDataLoading(false);
  }, [search, page]);

  // ── Fetch stats ────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    const result = await getWaitlistStats();
    if (!result.error) setStats(result);
    setStatsLoading(false);
  }, []);

  // ── Fetch chart data ───────────────────────────────────────────────────────
  const fetchChartData = useCallback(async (days = 14) => {
    setChartLoading(true);
    const { data } = await getSignupsByDay(days);
    setChartData(data);
    setChartLoading(false);
  }, []);

  // ── Initial load when authenticated ───────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUsers();
    fetchStats();
    fetchChartData();

    // Setup realtime subscription
    unsubRef.current = subscribeToWaitlist(
      (newUser) => {
        // Prepend new user to list without full refetch
        setUsers((prev) => [newUser, ...prev]);
        setStats((prev) => ({
          ...prev,
          total: prev.total + 1,
          today: prev.today + 1,
          thisWeek: prev.thisWeek + 1,
          thisMonth: prev.thisMonth + 1,
        }));
        setTotalCount((n) => n + 1);
        setNewSignupFlash(newUser);
        setTimeout(() => setNewSignupFlash(null), 4000);
      },
      (deleted) => {
        setUsers((prev) => prev.filter((u) => u.id !== deleted.id));
        setStats((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        setTotalCount((n) => Math.max(0, n - 1));
      }
    );
    setRealtimeActive(true);

    return () => {
      if (unsubRef.current) unsubRef.current();
      setRealtimeActive(false);
    };
  }, [isAuthenticated, fetchUsers, fetchStats, fetchChartData]);

  // Re-fetch when search or page changes
  useEffect(() => {
    if (isAuthenticated) fetchUsers();
  }, [search, page ,fetchUsers, isAuthenticated]);

  // ── User operations ────────────────────────────────────────────────────────
  const handleUpdateStatus = async (id, status) => {
    const { error } = await updateUserStatus(id, status);
    if (!error) {
      setUsers((prev) =>
        prev.map((u) => (u.id === id ? { ...u, status } : u))
      );
    }
    return { error };
  };

  const handleDeleteUser = async (id) => {
    const { error } = await deleteUser(id);
    if (!error) {
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setTotalCount((n) => Math.max(0, n - 1));
      setStats((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      setSelectedIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
    return { error };
  };

  // ── Bulk operations ────────────────────────────────────────────────────────
  const handleBulkUpdateStatus = async (status) => {
    const ids = [...selectedIds];
    const { error } = await bulkUpdateStatus(ids, status);
    if (!error) {
      setUsers((prev) =>
        prev.map((u) => (selectedIds.has(u.id) ? { ...u, status } : u))
      );
      setSelectedIds(new Set());
    }
    return { error };
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    const { error } = await bulkDeleteUsers(ids);
    if (!error) {
      setUsers((prev) => prev.filter((u) => !selectedIds.has(u.id)));
      setTotalCount((n) => Math.max(0, n - ids.length));
      setStats((prev) => ({ ...prev, total: Math.max(0, prev.total - ids.length) }));
      setSelectedIds(new Set());
    }
    return { error };
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === users.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map((u) => u.id)));
    }
  };

  // ── Export helpers ─────────────────────────────────────────────────────────
  const handleExportCSV = () => exportToCSV(users);

  const handleCopyEmails = async () => {
    await copyEmailsToClipboard(users);
  };

  // ── Search handler ─────────────────────────────────────────────────────────
  const handleSearch = (val) => {
    setSearch(val);
    setPage(0);
    setSelectedIds(new Set());
  };

  return {
    // Auth
    isAuthenticated, loginError, loginLoading, loginAttempts, login, logout,

    // Data
    users, totalCount, dataLoading, dataError,
    fetchUsers, fetchStats, fetchChartData,

    // Stats
    stats, statsLoading,

    // Chart
    chartData, chartLoading,

    // Search / pagination
    search, handleSearch,
    page, setPage, PAGE_SIZE,

    // Selection
    selectedIds, toggleSelect, toggleSelectAll,

    // Operations
    handleUpdateStatus, handleDeleteUser,
    handleBulkUpdateStatus, handleBulkDelete,

    // Export
    handleExportCSV, handleCopyEmails,

    // Realtime
    realtimeActive, newSignupFlash,
  };
}
